import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Plus,
  Globe,
  MapPin,
  Tag,
  Search,
  Edit2,
  Trash2,
  FileText,
  Download,
  ShieldCheck,
  Layers,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import { ContentPlanningProject, ContentPlanningPage } from '../../types';
import { Button } from '../UI/Button';
import { PageContentResearchWorkstation } from './PageContentResearchWorkstation';
import { PageOutlineWorkstation } from './PageOutlineWorkstation';
import { PageContentGenerationWorkstation } from './PageContentGenerationWorkstation';
import { ContentPlanningPdfService } from '../../services/contentPlanningPdfService';
import { KeywordResearchStorageService } from '../../services/keywordResearchStorageService';
import { ContentPlanningStorageService } from '../../services/contentPlanningStorageService';

interface ContentPlanningWorkspaceProps {
  project: ContentPlanningProject;
  onBack: () => void;
  onSaveProject: (project: ContentPlanningProject) => void;
}

export const ContentPlanningWorkspace: React.FC<ContentPlanningWorkspaceProps> = ({
  project,
  onBack,
  onSaveProject,
}) => {
  const [activeStepTab, setActiveStepTab] = useState<'step1_keywords' | 'step2_research' | 'step3_outline' | 'step4_generation'>('step1_keywords');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddPageModalOpen, setIsAddPageModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<ContentPlanningPage | null>(null);

  // Auto-sync missing variations from linked Keyword Research project on load
  useEffect(() => {
    const kwProjects = KeywordResearchStorageService.getAllProjects();
    let linkedKw = project.linkedKeywordProjectId ? kwProjects.find(k => k.id === project.linkedKeywordProjectId) : null;
    if (!linkedKw && kwProjects.length > 0) {
      linkedKw = kwProjects.find(k => k.clientName.toLowerCase() === project.clientName.toLowerCase() || k.websiteUrl === project.websiteUrl);
    }
    if (linkedKw) {
      const synced = ContentPlanningStorageService.syncProjectVariationsWithKeywordProject(project, linkedKw);
      if (synced !== project) {
        onSaveProject(synced);
      }
    }
  }, [project.id, project.linkedKeywordProjectId]);

  const handleManualSyncVariations = () => {
    const kwProjects = KeywordResearchStorageService.getAllProjects();
    let linkedKw = project.linkedKeywordProjectId ? kwProjects.find(k => k.id === project.linkedKeywordProjectId) : null;
    if (!linkedKw && kwProjects.length > 0) {
      linkedKw = kwProjects.find(k => k.clientName.toLowerCase() === project.clientName.toLowerCase() || k.websiteUrl === project.websiteUrl);
    }
    if (linkedKw) {
      const synced = ContentPlanningStorageService.syncProjectVariationsWithKeywordProject(project, linkedKw);
      onSaveProject(synced);
    }
  };

  // Form State for Page Add/Edit
  const [pageName, setPageName] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [pageType, setPageType] = useState<ContentPlanningPage['pageType']>('Service Page');
  const [status, setStatus] = useState<ContentPlanningPage['status']>('planned');

  const [primaryKeyword, setPrimaryKeyword] = useState('');
  const [primaryVariationsInput, setPrimaryVariationsInput] = useState('');

  const [secondaryKeyword, setSecondaryKeyword] = useState('');
  const [secondaryVariationsInput, setSecondaryVariationsInput] = useState('');

  const [tertiaryKeyword, setTertiaryKeyword] = useState('');
  const [tertiaryVariationsInput, setTertiaryVariationsInput] = useState('');

  const [notes, setNotes] = useState('');

  // Quick Add Variation State
  const [quickVarModal, setQuickVarModal] = useState<{
    pageId: string;
    tier: 'primary' | 'secondary' | 'tertiary';
    keywordName: string;
  } | null>(null);
  const [newQuickVar, setNewQuickVar] = useState('');

  // Open Modal for New Page
  const handleOpenAddModal = () => {
    setEditingPage(null);
    setPageName('');
    setTargetUrl('');
    setPageType('Service Page');
    setStatus('planned');

    setPrimaryKeyword('');
    setPrimaryVariationsInput('');

    setSecondaryKeyword('');
    setSecondaryVariationsInput('');

    setTertiaryKeyword('');
    setTertiaryVariationsInput('');

    setNotes('');
    setIsAddPageModalOpen(true);
  };

  // Open Modal for Edit Page
  const handleOpenEditModal = (page: ContentPlanningPage) => {
    setEditingPage(page);
    setPageName(page.pageName);
    setTargetUrl(page.targetUrl);
    setPageType(page.pageType || 'Service Page');
    setStatus(page.status || 'planned');

    setPrimaryKeyword(page.primaryKeyword || '');
    setPrimaryVariationsInput((page.primaryKeywordVariations || []).join(', '));

    setSecondaryKeyword(page.secondaryKeyword || '');
    setSecondaryVariationsInput((page.secondaryKeywordVariations || []).join(', '));

    setTertiaryKeyword(page.tertiaryKeyword || '');
    setTertiaryVariationsInput((page.tertiaryKeywordVariations || []).join(', '));

    setNotes(page.notes || '');
    setIsAddPageModalOpen(true);
  };

  // Helper to parse comma-separated string to array
  const parseVariations = (str: string): string[] => {
    return str
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
  };

  // Save Page (Add or Edit)
  const handleSavePage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pageName.trim()) return;

    const primVars = parseVariations(primaryVariationsInput);
    const secVars = parseVariations(secondaryVariationsInput);
    const tertVars = parseVariations(tertiaryVariationsInput);

    const updatedPages = [...(project.pages || [])];

    if (editingPage) {
      const idx = updatedPages.findIndex(p => p.id === editingPage.id);
      if (idx !== -1) {
        updatedPages[idx] = {
          ...editingPage,
          pageName: pageName.trim(),
          targetUrl: targetUrl.trim() || '/',
          pageType,
          status,
          primaryKeyword: primaryKeyword.trim(),
          primaryKeywordVariations: primVars,
          secondaryKeyword: secondaryKeyword.trim(),
          secondaryKeywordVariations: secVars,
          tertiaryKeyword: tertiaryKeyword.trim(),
          tertiaryKeywordVariations: tertVars,
          notes: notes.trim(),
        };
      }
    } else {
      const newPage: ContentPlanningPage = {
        id: `cp-page-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
        pageName: pageName.trim(),
        targetUrl: targetUrl.trim() || '/',
        pageType,
        status,
        primaryKeyword: primaryKeyword.trim(),
        primaryKeywordVariations: primVars,
        secondaryKeyword: secondaryKeyword.trim(),
        secondaryKeywordVariations: secVars,
        tertiaryKeyword: tertiaryKeyword.trim(),
        tertiaryKeywordVariations: tertVars,
        notes: notes.trim(),
      };
      updatedPages.push(newPage);
    }

    const updatedProj: ContentPlanningProject = {
      ...project,
      pages: updatedPages,
      updatedAt: new Date().toISOString(),
    };

    onSaveProject(updatedProj);
    setIsAddPageModalOpen(false);
  };

  // Delete Page
  const handleDeletePage = (pageId: string) => {
    const updatedPages = (project.pages || []).filter(p => p.id !== pageId);
    const updatedProj: ContentPlanningProject = {
      ...project,
      pages: updatedPages,
      updatedAt: new Date().toISOString(),
    };
    onSaveProject(updatedProj);
  };

  // Quick Add Variation Submit
  const handleQuickAddVariationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickVarModal || !newQuickVar.trim()) return;

    const { pageId, tier } = quickVarModal;
    const addedVar = newQuickVar.trim();

    const updatedPages = (project.pages || []).map(page => {
      if (page.id !== pageId) return page;

      if (tier === 'primary') {
        const existing = page.primaryKeywordVariations || [];
        if (!existing.includes(addedVar)) {
          return { ...page, primaryKeywordVariations: [...existing, addedVar] };
        }
      } else if (tier === 'secondary') {
        const existing = page.secondaryKeywordVariations || [];
        if (!existing.includes(addedVar)) {
          return { ...page, secondaryKeywordVariations: [...existing, addedVar] };
        }
      } else if (tier === 'tertiary') {
        const existing = page.tertiaryKeywordVariations || [];
        if (!existing.includes(addedVar)) {
          return { ...page, tertiaryKeywordVariations: [...existing, addedVar] };
        }
      }
      return page;
    });

    onSaveProject({
      ...project,
      pages: updatedPages,
      updatedAt: new Date().toISOString(),
    });

    setQuickVarModal(null);
    setNewQuickVar('');
  };

  // Export Content Plan JSON
  const handleExportPlan = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(project, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `content_plan_${project.clientName.toLowerCase().replace(/[^a-z0-9]/g, '_')}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  // Stats calculation
  const pagesList = project.pages || [];
  const totalPages = pagesList.length;

  const totalPrimaryVars = pagesList.reduce((acc, p) => acc + (p.primaryKeywordVariations?.length || 0), 0);
  const totalSecondaryVars = pagesList.reduce((acc, p) => acc + (p.secondaryKeywordVariations?.length || 0), 0);
  const totalTertiaryVars = pagesList.reduce((acc, p) => acc + (p.tertiaryKeywordVariations?.length || 0), 0);

  const filteredPages = pagesList.filter(p => {
    const q = searchQuery.toLowerCase();
    return (
      p.pageName.toLowerCase().includes(q) ||
      p.targetUrl.toLowerCase().includes(q) ||
      p.primaryKeyword.toLowerCase().includes(q) ||
      p.secondaryKeyword.toLowerCase().includes(q) ||
      p.tertiaryKeyword.toLowerCase().includes(q)
    );
  });

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px 100px 24px' }}>
      
      {/* 1. TOP NAVIGATION & HEADER */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'transparent',
            border: 'none',
            color: '#38bdf8',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            marginBottom: '16px',
            padding: 0,
          }}
        >
          <ArrowLeft size={16} />
          <span>Back to Content Planning Projects</span>
        </button>

        {/* Project Header Banner */}
        <div
          style={{
            background: '#090e1a',
            border: '1px solid #1e293b',
            borderRadius: '16px',
            padding: '28px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.01em' }}>
                {project.clientName}
              </h1>
              {project.linkedKeywordProjectId && (
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '3px 10px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Search size={12} /> Linked to Keyword Research
                </span>
              )}
              {project.linkedAuditId && (
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '3px 10px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <ShieldCheck size={12} /> Linked to Audit
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#94a3b8', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#38bdf8' }}>
                <Globe size={14} color="#38bdf8" />
                <a href={project.websiteUrl} target="_blank" rel="noreferrer" style={{ color: '#38bdf8', textDecoration: 'none' }}>
                  {project.websiteUrl}
                </a>
              </span>
              <span>•</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MapPin size={14} color="#f43f5e" />
                Target: {project.targetedLocation}
              </span>
              <span>•</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Tag size={14} color="#f59e0b" />
                Category: {project.category}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              onClick={handleManualSyncVariations}
              icon={<RefreshCw size={16} color="#38bdf8" />}
            >
              Sync Variations
            </Button>
            <Button
              variant="secondary"
              onClick={() => ContentPlanningPdfService.exportProjectPdfReport(project)}
              icon={<FileText size={16} color="#10b981" />}
            >
              Export PDF Strategy Report
            </Button>
            <Button variant="secondary" onClick={handleExportPlan} icon={<Download size={16} />}>
              Export JSON Plan
            </Button>
            <Button onClick={handleOpenAddModal} icon={<Plus size={16} />}>
              Add New Page
            </Button>
          </div>
        </div>

        {/* Step Navigation Tabs */}
        <div style={{ display: 'flex', gap: '12px', borderBottom: '1px solid #1e293b', paddingBottom: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveStepTab('step1_keywords')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              borderRadius: '10px',
              border: activeStepTab === 'step1_keywords' ? '1px solid #0284c7' : '1px solid #1e293b',
              background: activeStepTab === 'step1_keywords' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : '#070c16',
              color: '#ffffff',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <Layers size={16} color={activeStepTab === 'step1_keywords' ? '#ffffff' : '#38bdf8'} />
            <span>Step 1: Pages & Keyword Baseline</span>
          </button>

          <button
            onClick={() => setActiveStepTab('step2_research')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              borderRadius: '10px',
              border: activeStepTab === 'step2_research' ? '1px solid #0284c7' : '1px solid #1e293b',
              background: activeStepTab === 'step2_research' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : '#070c16',
              color: '#ffffff',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <Sparkles size={16} color={activeStepTab === 'step2_research' ? '#ffffff' : '#f59e0b'} />
            <span>Step 2: SERP & Competitor Heading Research</span>
          </button>

          <button
            onClick={() => setActiveStepTab('step3_outline')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              borderRadius: '10px',
              border: activeStepTab === 'step3_outline' ? '1px solid #0284c7' : '1px solid #1e293b',
              background: activeStepTab === 'step3_outline' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : '#070c16',
              color: '#ffffff',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <FileText size={16} color={activeStepTab === 'step3_outline' ? '#ffffff' : '#10b981'} />
            <span>Step 3: Content Strategy & Outline</span>
          </button>

          <button
            onClick={() => setActiveStepTab('step4_generation')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              borderRadius: '10px',
              border: activeStepTab === 'step4_generation' ? '1px solid #0284c7' : '1px solid #1e293b',
              background: activeStepTab === 'step4_generation' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : '#070c16',
              color: '#ffffff',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <Sparkles size={16} color={activeStepTab === 'step4_generation' ? '#ffffff' : '#38bdf8'} />
            <span>Step 4: Content Generation</span>
          </button>
        </div>
      </div>

      {activeStepTab === 'step2_research' ? (
        <PageContentResearchWorkstation project={project} onSaveProject={onSaveProject} />
      ) : activeStepTab === 'step3_outline' ? (
        <PageOutlineWorkstation project={project} onSaveProject={onSaveProject} />
      ) : activeStepTab === 'step4_generation' ? (
        <PageContentGenerationWorkstation project={project} onSaveProject={onSaveProject} onGoToStep3={() => setActiveStepTab('step3_outline')} />
      ) : (
        <>
      {/* 1. IMPORTED BASELINE EXPLANATION BANNER */}
      <div
        style={{
          background: 'rgba(2, 132, 199, 0.08)',
          border: '1px solid rgba(56, 189, 248, 0.25)',
          borderRadius: '12px',
          padding: '14px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Layers size={20} color="#38bdf8" style={{ flexShrink: 0 }} />
        <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.5' }}>
          <strong style={{ color: '#ffffff' }}>Imported Baseline from Keyword Research:</strong>{' '}
          This screen displays your planned pages and keyword architecture established in Keyword Research.
          Each page's <span style={{ color: '#38bdf8', fontWeight: 700 }}>Primary Keyword</span> serves as the designated <span style={{ color: '#38bdf8', fontWeight: 700 }}>Seed Keyword</span> for SERP competitor research in Step 2.
        </div>
      </div>

      {/* 2. SUMMARY METRIC CARDS */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '28px',
        }}
      >
        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>
            PLANNED PAGES
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#ffffff' }}>
            {totalPages} Pages
          </div>
          <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '4px' }}>
            Imported page inventory
          </div>
        </div>

        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase', marginBottom: '6px' }}>
            PRIMARY VARIATIONS
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#38bdf8' }}>
            {totalPrimaryVars} Variations
          </div>
          <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '4px' }}>
            Seed keyword variations
          </div>
        </div>

        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', marginBottom: '6px' }}>
            SECONDARY VARIATIONS
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#f59e0b' }}>
            {totalSecondaryVars} Variations
          </div>
          <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '4px' }}>
            Supporting context variations
          </div>
        </div>

        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#a855f7', textTransform: 'uppercase', marginBottom: '6px' }}>
            TERTIARY VARIATIONS
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#a855f7' }}>
            {totalTertiaryVars} Variations
          </div>
          <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '4px' }}>
            Supporting context variations
          </div>
        </div>
      </div>

      {/* 3. SEARCH & CONTROLS */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 300px', maxWidth: '400px' }}>
          <Search size={16} color="#64748b" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search planned pages or keywords..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px 10px 38px',
              borderRadius: '10px',
              background: '#070c16',
              border: '1px solid #1e293b',
              color: '#ffffff',
              fontSize: '13.5px',
              outline: 'none',
            }}
          />
        </div>

        <div style={{ fontSize: '13px', color: '#94a3b8' }}>
          Showing <span style={{ color: '#ffffff', fontWeight: 700 }}>{filteredPages.length}</span> of {totalPages} pages
        </div>
      </div>

      {/* 4. PAGES CONTENT CARDS */}
      {filteredPages.length === 0 ? (
        <div
          style={{
            background: '#070c16',
            border: '1px solid #1e293b',
            borderRadius: '16px',
            padding: '48px 24px',
            textAlign: 'center',
          }}
        >
          <FileText size={42} color="#334155" style={{ margin: '0 auto 16px auto' }} />
          <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: '0 0 8px 0' }}>
            No Pages Added Yet to Content Plan
          </h3>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px 0' }}>
            Add all the pages you are planning content for along with their Primary, Secondary, and Tertiary keywords and variations.
          </p>
          <Button onClick={handleOpenAddModal} icon={<Plus size={16} />}>
            Add Your First Page
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {filteredPages.map((page, index) => (
            <div
              key={page.id}
              style={{
                background: '#070c16',
                border: '1px solid #1e293b',
                borderRadius: '14px',
                padding: '24px',
                position: 'relative',
              }}
            >
              {/* Page Card Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '2px 8px', borderRadius: '6px' }}>
                      PAGE #{index + 1}
                    </span>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', background: '#090e1a', border: '1px solid #1e293b', padding: '2px 8px', borderRadius: '6px' }}>
                      {page.pageType || 'Service Page'}
                    </span>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: page.status === 'completed' ? '#10b981' : page.status === 'in_progress' ? '#f59e0b' : '#38bdf8', background: 'rgba(15, 23, 42, 0.8)', border: '1px solid #1e293b', padding: '2px 8px', borderRadius: '6px', textTransform: 'capitalize' }}>
                      ● {page.status?.replace('_', ' ') || 'Planned'}
                    </span>
                  </div>

                  <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
                    {page.pageName}
                  </h3>

                  <div style={{ fontSize: '13px', color: '#38bdf8', fontFamily: 'var(--font-mono)' }}>
                    Target URL: {page.targetUrl}
                  </div>
                </div>

                {/* Edit & Delete Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => handleOpenEditModal(page)}
                    style={{
                      background: 'rgba(2, 132, 199, 0.15)',
                      border: '1px solid rgba(56, 189, 248, 0.3)',
                      color: '#38bdf8',
                      borderRadius: '8px',
                      padding: '8px 14px',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <Edit2 size={14} />
                    <span>Edit Page & Keywords</span>
                  </button>

                  <button
                    onClick={() => handleDeletePage(page.id)}
                    title="Delete Page"
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      color: '#f87171',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Keyword Tier Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
                
                {/* 1. SEED / PRIMARY KEYWORD */}
                <div
                  style={{
                    background: '#090e1a',
                    border: '1px solid rgba(56, 189, 248, 0.35)',
                    borderRadius: '12px',
                    padding: '16px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        🎯 PRIMARY / SEED KEYWORD
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.25)', border: '1px solid rgba(56, 189, 248, 0.4)', padding: '1px 6px', borderRadius: '4px' }}>
                        Seed for SERP Research
                      </span>
                    </div>
                    <button
                      onClick={() => setQuickVarModal({ pageId: page.id, tier: 'primary', keywordName: page.primaryKeyword })}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#38bdf8',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Plus size={12} /> Add Variation
                    </button>
                  </div>

                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', marginBottom: '6px', background: 'rgba(2, 132, 199, 0.15)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.25)' }}>
                    {page.primaryKeyword || <span style={{ color: '#64748b', fontWeight: 500 }}>None set</span>}
                  </div>

                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px', fontStyle: 'italic' }}>
                    Used as the default seed keyword for competitor/SERP research in Step 2.
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: 700, marginBottom: '6px' }}>
                      Seed Variations ({page.primaryKeywordVariations?.length || 0}):
                    </div>
                    {(!page.primaryKeywordVariations || page.primaryKeywordVariations.length === 0) ? (
                      <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>No primary variations added yet</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {page.primaryKeywordVariations.map((v, vIdx) => (
                          <span
                            key={vIdx}
                            style={{
                              fontSize: '11.5px',
                              fontWeight: 600,
                              color: '#38bdf8',
                              background: 'rgba(56, 189, 248, 0.1)',
                              border: '1px solid rgba(56, 189, 248, 0.25)',
                              padding: '3px 8px',
                              borderRadius: '6px',
                            }}
                          >
                            {v}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. SECONDARY KEYWORD (SUPPORTING) */}
                <div
                  style={{
                    background: '#090e1a',
                    border: '1px solid rgba(245, 158, 11, 0.25)',
                    borderRadius: '12px',
                    padding: '16px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        ⚡ SECONDARY KEYWORD
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#f59e0b', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', padding: '1px 6px', borderRadius: '4px' }}>
                        Supporting Context
                      </span>
                    </div>
                    <button
                      onClick={() => setQuickVarModal({ pageId: page.id, tier: 'secondary', keywordName: page.secondaryKeyword })}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#f59e0b',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Plus size={12} /> Add Variation
                    </button>
                  </div>

                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', marginBottom: '6px', background: 'rgba(245, 158, 11, 0.1)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                    {page.secondaryKeyword || <span style={{ color: '#64748b', fontWeight: 500 }}>None set</span>}
                  </div>

                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px', fontStyle: 'italic' }}>
                    Provides topical support for outline generation in Step 3.
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: 700, marginBottom: '6px' }}>
                      Supporting Variations ({page.secondaryKeywordVariations?.length || 0}):
                    </div>
                    {(!page.secondaryKeywordVariations || page.secondaryKeywordVariations.length === 0) ? (
                      <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>No secondary variations added yet</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {page.secondaryKeywordVariations.map((v, vIdx) => (
                          <span
                            key={vIdx}
                            style={{
                              fontSize: '11.5px',
                              fontWeight: 600,
                              color: '#fbbf24',
                              background: 'rgba(245, 158, 11, 0.1)',
                              border: '1px solid rgba(245, 158, 11, 0.25)',
                              padding: '3px 8px',
                              borderRadius: '6px',
                            }}
                          >
                            {v}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 3. TERTIARY KEYWORD (SUPPORTING) */}
                <div
                  style={{
                    background: '#090e1a',
                    border: '1px solid rgba(168, 85, 247, 0.25)',
                    borderRadius: '12px',
                    padding: '16px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#a855f7', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        📌 TERTIARY KEYWORD
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#a855f7', background: 'rgba(168, 85, 247, 0.15)', border: '1px solid rgba(168, 85, 247, 0.3)', padding: '1px 6px', borderRadius: '4px' }}>
                        Supporting Context
                      </span>
                    </div>
                    <button
                      onClick={() => setQuickVarModal({ pageId: page.id, tier: 'tertiary', keywordName: page.tertiaryKeyword })}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#a855f7',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Plus size={12} /> Add Variation
                    </button>
                  </div>

                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', marginBottom: '6px', background: 'rgba(168, 85, 247, 0.1)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(168, 85, 247, 0.2)' }}>
                    {page.tertiaryKeyword || <span style={{ color: '#64748b', fontWeight: 500 }}>None set</span>}
                  </div>

                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px', fontStyle: 'italic' }}>
                    Provides topical support for outline generation in Step 3.
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: 700, marginBottom: '6px' }}>
                      Supporting Variations ({page.tertiaryKeywordVariations?.length || 0}):
                    </div>
                    {(!page.tertiaryKeywordVariations || page.tertiaryKeywordVariations.length === 0) ? (
                      <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>No tertiary variations added yet</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {page.tertiaryKeywordVariations.map((v, vIdx) => (
                          <span
                            key={vIdx}
                            style={{
                              fontSize: '11.5px',
                              fontWeight: 600,
                              color: '#c084fc',
                              background: 'rgba(168, 85, 247, 0.1)',
                              border: '1px solid rgba(168, 85, 247, 0.25)',
                              padding: '3px 8px',
                              borderRadius: '6px',
                            }}
                          >
                            {v}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {/* Page Notes */}
              {page.notes && (
                <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #1e293b', fontSize: '12.5px', color: '#94a3b8' }}>
                  <strong style={{ color: '#cbd5e1' }}>Notes:</strong> {page.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {/* 5. ADD / EDIT PAGE MODAL */}
      {isAddPageModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#090e1a',
              border: '1px solid #1e293b',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '680px',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '28px',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.7)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                {editingPage ? 'Edit Planned Page & Keywords' : 'Add New Planned Page'}
              </h2>
              <button
                onClick={() => setIsAddPageModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '20px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSavePage} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Page Details Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                    Page Name / Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Emergency Plumbing Service"
                    value={pageName}
                    onChange={e => setPageName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      background: '#070c16',
                      border: '1px solid #1e293b',
                      color: '#ffffff',
                      fontSize: '13.5px',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                    Target URL / Slug
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. /services/emergency-plumbing/"
                    value={targetUrl}
                    onChange={e => setTargetUrl(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      background: '#070c16',
                      border: '1px solid #1e293b',
                      color: '#ffffff',
                      fontSize: '13.5px',
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                    Page Type
                  </label>
                  <select
                    value={pageType}
                    onChange={e => setPageType(e.target.value as any)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      background: '#070c16',
                      border: '1px solid #1e293b',
                      color: '#ffffff',
                      fontSize: '13.5px',
                    }}
                  >
                    <option value="Homepage">Homepage</option>
                    <option value="Service Page">Service Page</option>
                    <option value="Location Page">Location Page</option>
                    <option value="Category Hub">Category Hub</option>
                    <option value="Blog Post">Blog Post</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                    Content Planning Status
                  </label>
                  <select
                    value={status}
                    onChange={e => setStatus(e.target.value as any)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      background: '#070c16',
                      border: '1px solid #1e293b',
                      color: '#ffffff',
                      fontSize: '13.5px',
                    }}
                  >
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <hr style={{ borderColor: '#1e293b', margin: '4px 0' }} />

              {/* 1. PRIMARY / SEED KEYWORD */}
              <div style={{ background: 'rgba(2, 132, 199, 0.08)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#38bdf8', margin: 0, textTransform: 'uppercase' }}>
                    🎯 Primary / Seed Keyword & Variations
                  </h4>
                  <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>
                    Seed for SERP Research
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#cbd5e1', marginBottom: '4px' }}>
                      Primary / Seed Keyword (Used for Step 2 SERP Research)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. emergency plumber miami"
                      value={primaryKeyword}
                      onChange={e => setPrimaryKeyword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '8px',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        color: '#ffffff',
                        fontSize: '13px',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#cbd5e1', marginBottom: '4px' }}>
                      Seed Keyword Variations (comma-separated)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. emergency plumbing miami, 24/7 plumber miami, urgent plumbing service"
                      value={primaryVariationsInput}
                      onChange={e => setPrimaryVariationsInput(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '8px',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        color: '#ffffff',
                        fontSize: '13px',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* 2. SECONDARY KEYWORD (SUPPORTING) */}
              <div style={{ background: 'rgba(245, 158, 11, 0.08)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#f59e0b', margin: 0, textTransform: 'uppercase' }}>
                    ⚡ Secondary Keyword & Variations
                  </h4>
                  <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#f59e0b', background: 'rgba(245, 158, 11, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>
                    Supporting Context
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#cbd5e1', marginBottom: '4px' }}>
                      Secondary Keyword (Topical Context for Step 3)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 24 hour plumbing repair"
                      value={secondaryKeyword}
                      onChange={e => setSecondaryKeyword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '8px',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        color: '#ffffff',
                        fontSize: '13px',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#cbd5e1', marginBottom: '4px' }}>
                      Secondary Variations (comma-separated)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 24 hour drain unblocking, late night plumber"
                      value={secondaryVariationsInput}
                      onChange={e => setSecondaryVariationsInput(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '8px',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        color: '#ffffff',
                        fontSize: '13px',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* 3. TERTIARY KEYWORD (SUPPORTING) */}
              <div style={{ background: 'rgba(168, 85, 247, 0.08)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(168, 85, 247, 0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#a855f7', margin: 0, textTransform: 'uppercase' }}>
                    📌 Tertiary Keyword & Variations
                  </h4>
                  <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#a855f7', background: 'rgba(168, 85, 247, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>
                    Supporting Context
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#cbd5e1', marginBottom: '4px' }}>
                      Tertiary Keyword (Topical Context for Step 3)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. burst pipe emergency fix"
                      value={tertiaryKeyword}
                      onChange={e => setTertiaryKeyword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '8px',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        color: '#ffffff',
                        fontSize: '13px',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#94a3b8', marginBottom: '4px' }}>
                      Tertiary Variations (comma-separated)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. leaking pipe repair cost, emergency water leak repair"
                      value={tertiaryVariationsInput}
                      onChange={e => setTertiaryVariationsInput(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '8px',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        color: '#ffffff',
                        fontSize: '13px',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                  Notes / Brief Recommendations
                </label>
                <textarea
                  rows={3}
                  placeholder="Additional context or brief instructions for this page..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#070c16',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '13.5px',
                    resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '10px' }}>
                <Button type="button" variant="secondary" onClick={() => setIsAddPageModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPage ? 'Update Page & Keywords' : 'Add Page to Content Plan'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. QUICK ADD VARIATION MODAL */}
      {quickVarModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 110,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#090e1a',
              border: '1px solid #1e293b',
              borderRadius: '14px',
              width: '100%',
              maxWidth: '440px',
              padding: '24px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)',
            }}
          >
            <h3 style={{ fontSize: '17px', fontWeight: 800, color: '#ffffff', margin: '0 0 6px 0' }}>
              Add Variation to {quickVarModal.tier.toUpperCase()} Keyword
            </h3>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 16px 0' }}>
              Base Keyword: <span style={{ color: '#ffffff', fontWeight: 700 }}>{quickVarModal.keywordName || 'Unspecified'}</span>
            </p>

            <form onSubmit={handleQuickAddVariationSubmit}>
              <input
                type="text"
                required
                autoFocus
                placeholder="e.g. truck spare parts supplier"
                value={newQuickVar}
                onChange={e => setNewQuickVar(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  background: '#070c16',
                  border: '1px solid #1e293b',
                  color: '#ffffff',
                  fontSize: '13.5px',
                  marginBottom: '16px',
                }}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button type="button" variant="secondary" onClick={() => setQuickVarModal(null)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Add Variation
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7. PERSISTENT BOTTOM NAVIGATION BAR */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(9, 14, 26, 0.95)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid #1e293b',
          padding: '14px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 90,
          boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '12px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Workflow Navigation:
          </span>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', background: '#070c16', border: '1px solid #1e293b', padding: '4px 12px', borderRadius: '8px' }}>
            {activeStepTab === 'step1_keywords' && 'Step 1 of 4: Pages & Keyword Architecture'}
            {activeStepTab === 'step2_research' && 'Step 2 of 4: Headings & Pain Points Research'}
            {activeStepTab === 'step3_outline' && 'Step 3 of 4: Content Strategy & Outline'}
            {activeStepTab === 'step4_generation' && 'Step 4 of 4: Content Generation'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {activeStepTab === 'step1_keywords' && (
            <button
              onClick={() => {
                setActiveStepTab('step2_research');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '10px 22px',
                borderRadius: '10px',
                fontSize: '13.5px',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 14px rgba(2, 132, 199, 0.4)',
              }}
            >
              <span>Next: Step 2 - Research Headings & Pain Points</span>
              <ArrowRight size={16} />
            </button>
          )}

          {activeStepTab === 'step2_research' && (
            <>
              <button
                onClick={() => {
                  setActiveStepTab('step1_keywords');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: '#070c16',
                  color: '#94a3b8',
                  border: '1px solid #1e293b',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <ArrowLeft size={14} />
                <span>Back to Step 1</span>
              </button>

              <button
                onClick={() => {
                  setActiveStepTab('step3_outline');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '10px 22px',
                  borderRadius: '10px',
                  fontSize: '13.5px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.4)',
                }}
              >
                <span>Next: Step 3 - Content Strategy & Outline</span>
                <ArrowRight size={16} />
              </button>
            </>
          )}

          {activeStepTab === 'step3_outline' && (
            <>
              <button
                onClick={() => {
                  setActiveStepTab('step2_research');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: '#070c16',
                  color: '#94a3b8',
                  border: '1px solid #1e293b',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <ArrowLeft size={14} />
                <span>Back to Step 2</span>
              </button>

              <button
                onClick={() => {
                  setActiveStepTab('step4_generation');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '10px 22px',
                  borderRadius: '10px',
                  fontSize: '13.5px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.4)',
                }}
              >
                <span>Next: Step 4 - Content Generation</span>
                <ArrowRight size={16} />
              </button>
            </>
          )}

          {activeStepTab === 'step4_generation' && (
            <>
              <button
                onClick={() => {
                  setActiveStepTab('step3_outline');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: '#070c16',
                  color: '#94a3b8',
                  border: '1px solid #1e293b',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <ArrowLeft size={14} />
                <span>Back to Step 3</span>
              </button>

              <button
                onClick={handleExportPlan}
                style={{
                  background: '#070c16',
                  color: '#94a3b8',
                  border: '1px solid #1e293b',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Download size={14} />
                <span>Export JSON Plan</span>
              </button>

              <button
                onClick={() => ContentPlanningPdfService.exportProjectPdfReport(project)}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '10px 22px',
                  borderRadius: '10px',
                  fontSize: '13.5px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
                }}
              >
                <FileText size={16} />
                <span>Finish & Export PDF Strategy Report</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
